// format large numbers for activity metric card counts
export const formatCount = (num) => {
  if (num === null || num === undefined || !Number.isFinite(num)) return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(3).replace(/\.?0+$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return num.toString();
};

// build N-30 date range for activity charts 
export const buildDateRange = (dailyCounts, days = 30, defaultValues = { count: 0 }) => {
  const allDates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    allDates.push(date.toISOString().split('T')[0]);
  }

  const dataMap = {};
  dailyCounts.forEach(d => {
    dataMap[d.date] = d;
  });

  return allDates.map(date => ({
    date,
    ...defaultValues,
    ...(dataMap[date] || {})
  }));
};

export function getIdenfyValueFromPerl(perlInt){
  switch (parseInt(perlInt)) {
    case 2:
      return 'pending';
    case 1:
      return 'complete';
    case 3:
      return 'denied';
    case 4:
      return 'blocked';
    case 5: 
      return 'aml-blocked';
    case 6:
      return 'internal-review';
    case 0:
      return 'not-initiated';
    case 7:
      return 'expired';
    case 8:
      return 'waived';
    default:
      return 'unknown';
  }
}

export function getPerlValueFromStatus(thisStatus){
  switch (thisStatus) {
    case 'pending':
      return 2;
    case 'complete':
      return 1;
    case 'denied':
      return 3;
    case 'blocked':
      return 4;
    case 'aml-blocked':
      return 5; 
    case 'internal-review':
      return 6;
    case 'not-initiated':
      return 0;
    case 'expired':
      return 7;
    case 'waived':
      return 8;
    default:
      return -1;
  }
}

export function getStudioStatusFromPerl(perlInt) {
  switch (parseInt(perlInt)) {
    case 0:
      return 'Pending';
    case 1:
      return 'Approved';
    case 2: 
      return 'Denied';
    case 3: 
      return 'Suspended';
    case 4: 
      return 'Suspended twice';
    case 5: 
      return 'Cancelled';
    case 6: 
      return 'Closed';
    default:
      return 'Unknown';
  }
}

/* KYC Statuses */
export const idenfyStatuses = [
  {desc: 'pending', int_value: 2}, 
  {desc: 'complete', int_value: 1},
  {desc: 'denied', int_value: 3},
  {desc: 'blocked', int_value: 4},
  {desc: 'aml-blocked', int_value: 5},
  {desc: 'internal-review', int_value: 6},
  {desc: 'not-initiated', int_value: 0},
  {desc: 'expired', int_value: 7},
  {desc: 'waived', int_value: 8},
  {desc: 'unknown', int_value: -1}
]

export const KYBStatuses = [
  {desc: 'Pending', str_value: 'PENDING'},
  {desc: 'Submitted', str_value: 'SUBMITTED'},
  {desc: 'Approved', str_value: 'APPROVED'},
  {desc: 'KYC Blocked', str_value: 'KYB_APPROVED_KYC_BLOCK'},
  {desc: 'Denied', str_value: 'DENIED'}
]

export const LegalStatuses = [
  {desc: 'Pending', str_value: 'PENDING'},
  {desc: 'Approved', str_value: 'APPROVED'},
  {desc: 'Legal Override Approved', str_value: 'LEGAL_OVERRIDE_APPROVED'},
  {desc: 'Denied', str_value: 'DENIED'},
  {desc: 'Expired', str_value: 'EXPIRED'}
]

/// ROLES

import { store } from '../app/store';
import { api }  from '../app/api';

export const getRole = async (thisUUID) => {
  try {
    const thisRole = await store.dispatch(
      api.endpoints.adminRole.initiate({uuid: thisUUID})
    );
    return thisRole?.data?.admin_role?.role || 'PENDING'; // Default to 'PENDING' if no role found
  } catch (error) {
    console.error("Error fetching admin role:", error);
    return 'PENDING'; // Fallback in case of error
  }
}

export const isCSRole = async (UUID) => {
  const role = await getRole(UUID);
  return checkForCS(role);
}

export const checkForCS = (role) => {
  return role === 'CS' || role === 'LEGAL' || role === 'ADMIN' || role === 'CS_ADMIN';
}

export const isLegalRole = async (UUID) => {
  const role = await getRole(UUID);
  return checkForLegal(role);
}

export const checkForLegal = (role) => {
  return role === 'LEGAL' || role === 'ADMIN';
}

export const isCSAdminRole = (role) => {
  return role === 'CS_ADMIN' || role === 'ADMIN';
}

export const isAdminRole = async (UUID) => {
  const role = await getRole(UUID);
  return role === 'ADMIN';
}


export const getRoleJSON = async (UUID) => {
  const role = await getRole(UUID);
  return {
    role: role,
    isCS: checkForCS(role),
    isLegal: checkForLegal(role), 
    isCSAdmin: isCSAdminRole(role), 
    isAdmin: role === 'ADMIN'
  };
}

/**
 * Convert JSON data to a File object (image)
 * @param {Object|Array} jsonData - The JSON data to convert
 * @param {string} filename - The filename for the generated file (default: 'json-data.png')
 * @param {Object} options - Styling options for the image
 * @returns {Promise<File>} Promise that resolves to a File object
 */
export const jsonToImageFile = (jsonData, filename = 'json-data.png', options = {}) => {
  const {
    width = 800,
    backgroundColor = '#ffffff',
    textColor = '#000000',
    fontSize = 22,
    fontFamily = 'Arial, sans-serif',
    padding = 20,
    lineHeight = 2,
    indentSize = 20
  } = options;

  // Helper function to check if a value should be included
  const shouldIncludeValue = (value) => {
    // Exclude null, undefined
    if (value === null || value === undefined) return false;
    
    // Include numbers
    if (typeof value === 'number') return true;
    
    // For strings, exclude empty strings
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    
    // For arrays, only include if not empty and process items
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    
    // For objects, only include if not empty and has valid properties
    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }
    
    // Exclude all other types (booleans, etc.)
    return false;
  };

  // Convert JSON to human-readable format
  const formatJsonToReadable = (obj, indent = 0) => {
    const lines = [];
    const indentStr = ' '.repeat(indent);

    if (Array.isArray(obj)) {
      // Filter array to only include valid items
      const validItems = obj.filter(item => shouldIncludeValue(item));
      
      validItems.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          const nestedLines = formatJsonToReadable(item, indent + indentSize);
          if (nestedLines.length > 0) {
            lines.push(`${indentStr}Item ${index + 1}:`);
            lines.push(...nestedLines);
          }
        } else if (typeof item === 'string' || typeof item === 'number') {
          lines.push(`${indentStr}Item ${index + 1}: ${item}`);
        }
      });
    } else if (typeof obj === 'object' && obj !== null) {
      Object.entries(obj).forEach(([key, value]) => {
        if (!shouldIncludeValue(value)) return; // Skip invalid values
        
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        
        if (typeof value === 'object' && value !== null) {
          const nestedLines = formatJsonToReadable(value, indent + indentSize);
          if (nestedLines.length > 0) {
            lines.push(`${indentStr}${formattedKey}:`);
            lines.push(...nestedLines);
          }
        } else if (typeof value === 'string' || typeof value === 'number') {
          lines.push(`${indentStr}${formattedKey}: ${value}`);
        }
      });
    }

    return lines;
  };

  return new Promise((resolve, reject) => {
    try {
      // Create canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Convert JSON to readable format
      const lines = formatJsonToReadable(jsonData);

      // Set fonts for measurement
      const normalFont = `${fontSize}px ${fontFamily}`;
      const boldFont = `bold ${fontSize}px ${fontFamily}`;

      // Calculate dimensions
      ctx.font = boldFont; // Use bold font for measurement (widest case)
      const maxLineWidth = Math.max(...lines.map(line => {
        // Measure the full line with bold values
        const colonIndex = line.indexOf(': ');
        if (colonIndex > -1) {
          const keyPart = line.substring(0, colonIndex + 2);
          const valuePart = line.substring(colonIndex + 2);
          ctx.font = normalFont;
          const keyWidth = ctx.measureText(keyPart).width;
          ctx.font = boldFont;
          const valueWidth = ctx.measureText(valuePart).width;
          return keyWidth + valueWidth;
        } else {
          ctx.font = normalFont;
          return ctx.measureText(line).width;
        }
      }));

      const actualWidth = Math.max(width, maxLineWidth + (padding * 2));
      const actualLineHeight = fontSize * lineHeight;
      const totalHeight = (lines.length * actualLineHeight) + (padding * 2);

      // Set canvas size
      canvas.width = actualWidth;
      canvas.height = totalHeight;

      // Fill background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, actualWidth, totalHeight);

      // Set text properties
      ctx.fillStyle = textColor;
      ctx.textBaseline = 'top';

      // Draw each line with proper formatting
      lines.forEach((line, index) => {
        const y = padding + (index * actualLineHeight);
        const colonIndex = line.indexOf(': ');
        
        if (colonIndex > -1) {
          // Line has a key-value pair
          const keyPart = line.substring(0, colonIndex + 2); // Include ": "
          const valuePart = line.substring(colonIndex + 2);
          
          // Draw key part in normal font
          ctx.font = normalFont;
          ctx.fillText(keyPart, padding, y);
          
          // Calculate where value starts
          const keyWidth = ctx.measureText(keyPart).width;
          
          // Draw value part in bold font
          ctx.font = boldFont;
          ctx.fillText(valuePart, padding + keyWidth, y);
        } else {
          // Line is a header/section (draw in normal font)
          ctx.font = normalFont;
          ctx.fillText(line, padding, y);
        }
      });

      // Convert canvas to blob, then to File
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], filename, { type: 'image/png' });
          resolve(file);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/png');

    } catch (error) {
      reject(error);
    }
  });
};